import React from 'react';
import { Chip, Avatar, Tooltip } from '@heroui/react';
import { ResponsiveTooltip, Typography } from '../../../../components';
import { InfoCircle } from '../../../../components/icons';
import { AccountCellProps } from '../types';
import { useTranslations } from '../../../../contexts/translation.context';
import { useAuth } from '../../../../contexts/AuthContext';
import { formatTimeAgo } from '../../../../utils/formatTimeAgo';

const AccountCell: React.FC<AccountCellProps> = ({ offer, showStatus = true }) => {
  const { t } = useTranslations();
  const { user } = useAuth();

  const ofData = offer?.of_data || {};
  const blockedCountries = ofData?.blocked_countries || [];

  // Проверяем, принадлежит ли оффер текущему пользователю
  const userId = user?.id;
  const ownerId = typeof offer.owner === 'string' ? offer.owner : offer.owner?._id;
  const isMyOffer = (offer as any).priority === 1 || (userId && ownerId === userId);

  const parseNumber = (value: unknown) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const formatCurrency = (value: unknown) => `$${parseNumber(value).toFixed(2)}`;
  const formatInteger = (value: unknown) => parseNumber(value).toLocaleString();

  const renderTooltipContent = () => {
    const balance = formatCurrency(ofData?.balance);
    const subscribers = formatInteger(ofData?.subscribers ?? offer?.subscribers);
    const favorited = formatInteger(ofData?.favorited ?? offer?.favorited);
    const posts = formatInteger(ofData?.posts ?? offer?.posts);
    const blockedCountriesCount = blockedCountries.length;

    return (
      <div className="bg-white border border-light rounded-3 p-0 min-w-[17.5rem]">
        {/* Header */}
        <div className="px-4 pt-4 pb-0">
          <Typography
            variant="body2"
            className="text-auth-ink text-sm font-normal leading-[1.125rem]"
          >
            {t('accounts.generalInfo')}
          </Typography>
        </div>

        {/* Stats */}
        <div className="flex flex-col gap-1 px-4 py-0">
          {/* Balance */}
          <div className="flex items-center justify-between py-1.5">
            <Typography variant="body3" className="text-neutral-600 text-xs font-normal leading-4">
              {t('accounts.balance')}
            </Typography>
            <Typography variant="body3" className="text-neutral-900 text-xs font-medium leading-4">
              {balance}
            </Typography>
          </div>

          {/* Subscribers */}
          <div className="flex items-center justify-between py-1.5">
            <Typography variant="body3" className="text-neutral-600 text-xs font-normal leading-4">
              {t('accounts.subscribers')}
            </Typography>
            <Typography variant="body3" className="text-neutral-900 text-xs font-medium leading-4">
              {subscribers}
            </Typography>
          </div>

          {/* Favorited */}
          <div className="flex items-center justify-between py-1.5">
            <Typography variant="body3" className="text-neutral-600 text-xs font-normal leading-4">
              {t('accounts.likes')}
            </Typography>
            <Typography variant="body3" className="text-neutral-900 text-xs font-medium leading-4">
              {favorited}
            </Typography>
          </div>

          {/* Posts */}
          <div className="flex items-center justify-between py-1.5">
            <Typography variant="body3" className="text-neutral-600 text-xs font-normal leading-4">
              {t('accounts.posts')}
            </Typography>
            <Typography variant="body3" className="text-neutral-900 text-xs font-medium leading-4">
              {posts}
            </Typography>
          </div>

          {/* Blocked countries */}
          <div className="flex items-center justify-between py-1.5">
            <Typography variant="body3" className="text-neutral-600 text-xs font-normal leading-4">
              {t('accounts.blockedCountries')}
            </Typography>
            <Typography variant="body3" className="text-neutral-900 text-xs font-medium leading-4">
              {blockedCountriesCount}
            </Typography>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 pb-4">
          <Typography variant="body4" className="text-neutral-600 text-xxs font-normal leading-4">
            {ofData?.updated_at
              ? t('accounts.updatedAgo', { time: formatTimeAgo(ofData.updated_at, t) })
              : t('accounts.noUpdateInfo')}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <div className="flex items-start gap-3">
      <div className="min-w-10 mt-[0.1875rem] max-h-10 min-h-10 max-w-10">
        <Avatar
          src={offer.avatar}
          name={offer.name || offer.of_user_name}
          className="min-w-10 min-h-10 max-w-10 max-h-10 rounded-xl"
        />
      </div>
      <div>
        <div className="flex items-center gap-x-2 gap-y-0 flex-wrap">
          <Typography variant="label2" className="whitespace-nowrap">
            {offer.name}
          </Typography>
          <div className="flex items-center gap-0.5 flex-nowrap">
            <a
              target="__blank"
              href={`https://onlyfans.com/${offer.of_user_name}`}
              className="text-blue-600 hover:underline flex items-center gap-1 whitespace-nowrap"
            >
              <Typography variant="body2" className="!text-partner">
                @{offer.of_user_name}
              </Typography>
            </a>
            {isMyOffer && (
              <ResponsiveTooltip
                content={renderTooltipContent()}
                placement="bottom"
                showArrow={false}
                classNames={{
                  content: 'p-0 bg-transparent shadow-none',
                }}
              >
                <div className="cursor-pointer">
                  <InfoCircle />
                </div>
              </ResponsiveTooltip>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-1">
          {/* Тип аккаунта (FREE/PAID) */}
          <ResponsiveTooltip content={t('revshare.table.subscriptionTypeTooltip')} placement="top">
            <Chip
              size="sm"
              variant="flat"
              classNames={{
                base: 'bg-partner/20',
                content: `text-partner ${offer.type === 'FREE' ? 'uppercase' : 'capitalize'}`,
              }}
            >
              {offer.type}
            </Chip>
          </ResponsiveTooltip>

          {/* Performer TOP % (оранжевая плашка) */}
          {offer.overall_popularity_percent !== undefined &&
            offer.overall_popularity_percent !== null && (
              <ResponsiveTooltip content={t('revshare.table.topCreatorsTooltip')} placement="top">
                <Chip
                  size="sm"
                  variant="flat"
                  classNames={{
                    base: 'bg-warning/20',
                    content: 'text-warning',
                  }}
                >
                  {typeof offer.overall_popularity_percent === 'number'
                    ? `${offer.overall_popularity_percent.toFixed(1)}%`
                    : `${offer.overall_popularity_percent}%`}
                </Chip>
              </ResponsiveTooltip>
            )}

          {/* Статус аккаунта OnlyFans */}
          {showStatus && offer.status === 'active' && (
            <ResponsiveTooltip content={t('revshare.table.accountStatusTooltip')} placement="top">
              <Chip
                size="sm"
                variant="flat"
                classNames={{
                  base: 'bg-success/20',
                  content: 'text-success',
                }}
              >
                {t('revshare.table.accessActive') || 'Доступ активен'}
              </Chip>
            </ResponsiveTooltip>
          )}

          {/* Статус оффера - ожидание */}
          {/* {(offer.offer?.status === 'MODERATION' || offer.offer?.status === 'moderation' || offer.status === 'moderation') && (
            <Chip
              size="sm"
              variant="flat"
              classNames={{
                base: 'bg-auth-strength-weak/20',
                content: 'text-auth-strength-weak'
              }}
            >
              {t('revshare.table.waitingAccess')}
            </Chip>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default AccountCell;
